import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PropTypes } from 'prop-types';
import { useAddProducts } from 'pages/Looks/hooks/useAddProducts';
import { useFetchParentProductsInfinite } from 'api/products/hooks';
import * as Ui from './AddProductsModal.styles';
import ProductSearch from './components/ProductSearch';
import AddProductsModalFooter from './components/AddProductsModalFooter';
import AvailableProductsList from './components/AvailableProductsList';
import SelectedProductsList from './components/SelectedProductsList';

const AddProductsModal = ({ isOpen, onAdd, regionUid, lookProducts, onModalClose }) => {
  const { t } = useTranslation('ownerPortal');

  const [search, setSearch] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const {
    data: availableProductsData,
    fetchNextPage: fetchNextAvailableProducts,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useFetchParentProductsInfinite({
    pageSize: 30,
    search,
    filters: {
      region: regionUid,
      ...(showFavoritesOnly ? { is_favorite: true } : {}),
    },
  });

  const availableProducts = availableProductsData?.pages.flatMap((page) => page.results) || [];

  const { productsToAdd, addProduct, removeProduct, reset } = useAddProducts(
    lookProducts,
    availableProducts,
  );

  const handleSave = (closeModal) => {
    onAdd(productsToAdd);
    closeModal?.();
  };

  const handleClose = () => {
    reset();
    onModalClose();
  };

  return (
    <Ui.StyledModal
      open={isOpen}
      header={{ title: t('Looks.addProductsTitle') }}
      closeOnDocumentClick
      onClose={handleClose}
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        paddingBottom: 0,
      }}
      footer={(closePopup) => (
        <AddProductsModalFooter
          productsToAdd={productsToAdd}
          onRemove={removeProduct}
          onSave={() => handleSave(closePopup)}
          onCancel={handleClose}
        />
      )}
    >
      <Ui.ModalBody>
        <ProductSearch
          setSearch={setSearch}
          showFavoritesOnly={showFavoritesOnly}
          onToggleFavoriteFilter={() => setShowFavoritesOnly((value) => !value)}
          onResetFavoriteFilter={() => setShowFavoritesOnly(false)}
        />
        <Ui.Layout>
          <AvailableProductsList
            products={availableProducts}
            hasNextPage={hasNextPage}
            addedProducts={productsToAdd}
            onAdd={addProduct}
            isLoading={isLoading}
            isLoadingNextPage={isFetchingNextPage}
            fetchNextAvailableProducts={fetchNextAvailableProducts}
          />

          <SelectedProductsList products={productsToAdd} onRemove={removeProduct} />
        </Ui.Layout>
      </Ui.ModalBody>
    </Ui.StyledModal>
  );
};

AddProductsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onModalClose: PropTypes.func.isRequired,
  lookProducts: PropTypes.array,
};

AddProductsModal.defaultProps = {
  lookProducts: [],
};

export default AddProductsModal;
